import React, { useState, useEffect, useCallback } from 'react';
import './contact.css';
import useAutoTranslate from '../../hooks/useAutoTranslate';
import fr from './langue/fr.json';
import en from './langue/en.json';
import { FaPaperPlane, FaUser, FaEnvelope, FaTag, FaCommentAlt } from 'react-icons/fa';

const ENDPOINT       = 'https://baptistelemattre.fr/api/contact.php';
const RECAPTCHA_KEY  = '6LefFZ4sAAAAAEl2cHMeYgrl77MA1B3N4Svz0YtN'; // 🔧 à remplacer

const INITIAL = { nom: '', email: '', sujet: '', message: '', website: '' };

function Contact() {
  const t = useAutoTranslate({ fr, en });
  const [form, setForm]     = useState(INITIAL);
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});

  /* ── Chargement du script reCAPTCHA v3 ── */
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  /* ── Obtenir le token reCAPTCHA ── */
  const getRecaptchaToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) return reject('reCAPTCHA non chargé');
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_KEY, { action: 'contact' })
          .then(resolve)
          .catch(reject);
      });
    });
  }, []);

  /* ── Validation côté client ── */
  const validate = () => {
    const e = {};
    if (!form.nom.trim())                                 e.nom     = t('errors.nom');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email   = t('errors.email');
    if (!form.sujet.trim())                               e.sujet   = t('errors.sujet');
    if (form.message.trim().length < 10)                  e.message = t('errors.message');
    return e;
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(err => ({ ...err, [e.target.name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot
    if (form.website) return;

    const v = validate();
    if (Object.keys(v).length) { setErrors(v); return; }

    setStatus('loading');
    try {
      // Récupération du token reCAPTCHA
      const recaptchaToken = await getRecaptchaToken();

      const res = await fetch(ENDPOINT, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ ...form, recaptchaToken }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setForm(INITIAL);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="contact" id="contact" aria-labelledby="contact-heading">
      <div className="section-container">
        <h2 id="contact-heading" className="section-heading">{t('heading')}</h2>
        <p className="contact__sub">{t('sub')}</p>

        <div className="contact__form" noValidate>

          {/* ── HONEYPOT anti-bot ── */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={handleChange}
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
          />

          {/* NOM */}
          <div className={`contact__field ${errors.nom ? 'contact__field--error' : ''}`}>
            <label htmlFor="nom"><FaUser /> {t('fields.nom')}</label>
            <input
              id="nom" name="nom" type="text"
              autoComplete="name" maxLength={100}
              value={form.nom} onChange={handleChange}
              placeholder={t('placeholders.nom')}
            />
            {errors.nom && <span className="contact__error">{errors.nom}</span>}
          </div>

          {/* EMAIL */}
          <div className={`contact__field ${errors.email ? 'contact__field--error' : ''}`}>
            <label htmlFor="email"><FaEnvelope /> {t('fields.email')}</label>
            <input
              id="email" name="email" type="email"
              autoComplete="email" maxLength={150}
              value={form.email} onChange={handleChange}
              placeholder={t('placeholders.email')}
            />
            {errors.email && <span className="contact__error">{errors.email}</span>}
          </div>

          {/* SUJET */}
          <div className={`contact__field ${errors.sujet ? 'contact__field--error' : ''}`}>
            <label htmlFor="sujet"><FaTag /> {t('fields.sujet')}</label>
            <input
              id="sujet" name="sujet" type="text"
              maxLength={150}
              value={form.sujet} onChange={handleChange}
              placeholder={t('placeholders.sujet')}
            />
            {errors.sujet && <span className="contact__error">{errors.sujet}</span>}
          </div>

          {/* MESSAGE */}
          <div className={`contact__field contact__field--full ${errors.message ? 'contact__field--error' : ''}`}>
            <label htmlFor="message"><FaCommentAlt /> {t('fields.message')}</label>
            <textarea
              id="message" name="message"
              rows={6} maxLength={2000}
              value={form.message} onChange={handleChange}
              placeholder={t('placeholders.message')}
            />
            <span className="contact__count">{form.message.length}/2000</span>
            {errors.message && <span className="contact__error">{errors.message}</span>}
          </div>

          {/* SUBMIT */}
          <div className="contact__field contact__field--full contact__field--submit">
            <button
              type="button"
              className="contact__btn"
              onClick={handleSubmit}
              disabled={status === 'loading'}
            >
              {status === 'loading'
                ? <span className="contact__spinner" />
                : <><FaPaperPlane /> {t('send')}</>
              }
            </button>
          </div>

          {/* FEEDBACK */}
          {status === 'success' && (
            <p className="contact__feedback contact__feedback--success">{t('success')}</p>
          )}
          {status === 'error' && (
            <p className="contact__feedback contact__feedback--error">{t('error')}</p>
          )}

        </div>
      </div>
    </section>
  );
}

export default Contact;